import {useCallback, useEffect, useRef, useState} from "react";
import {definedValue} from "../../../../utils/input";
import {LocationListener, LocationValue} from "../props";
import {LocationChanged} from "../../LocationFilter/props";

const useLocationListeners = <T>() => {
  const listeners = useRef<LocationListener<T>[]>([]);
  useEffect(() => {
    return () => {
      listeners.current = [];
    }
  }, []);
  const addListener = useCallback((listener: LocationListener<T>) => {
    listeners.current = [...listeners.current, listener];
    return () => {
      listeners.current = listeners.current.filter(l => l !== listener);
    };
  }, []);

  const notify = useCallback((stuff: T) => {
    listeners.current.forEach(listener => listener(stuff));
  }, []);

  return {
    addListener,
    notify,
    listeners
  }
};

const useLocationRadius = (options: string[], originalIndex = 0) => {
  const [radiusIndex, setRadiusIndex] = useState(originalIndex);
  const { addListener: addRadiusListener, notify } = useLocationListeners();

  useEffect(() => {
    notify(radiusIndex);
  }, [radiusIndex, notify]);

  const setValue = useCallback((value: string) => {
    const index = options.indexOf(value);
    if (index !== -1) {
      setRadiusIndex(index);
    }
  }, [notify, options]);

  const getValue = useCallback(() => {
    return options[radiusIndex];
  }, [options, radiusIndex]);

  const onChange = useCallback((index: number) => {
    setValue(options[index]);
  }, [options, setValue]);

  return {
    setValue,
    getValue,
    addRadiusListener,
    setIndex: setRadiusIndex,
    radiusIndex,
    onChange,
    options
  };
}

const useLocationSearch = (search?: string) => {
  const fireSearch = useRef(false);
  const [searchedValue, setSearchedValue] = useState(() => {
    return definedValue(search, '');
  });
  const [coordinates, setCoordinates] = useState<LocationChanged>(null);
  const [searchOptions, setSearchOptions] = useState<string[]>([]);
  const { addListener, notify, listeners } = useLocationListeners<string>();

  useEffect(() => {
    if (fireSearch.current) {
      fireSearch.current = false;
      notify(searchedValue);
    }    
  }, [searchedValue, notify]);

  const setValue = useCallback((value: string) => {
    setSearchedValue(value);    
  }, []);

  const searchValue = useCallback((value: string) => {
    fireSearch.current = true;
    setSearchedValue(value);
  }, []);

  return {
    searchedValue,
    setValue,
    setSearchOptions,
    searchOptions,
    addSearchListener: addListener,
    setCoordinates,
    searchValue,
    coordinates,
    // let's keep it exposed to debug
    searchListeners: listeners
  };
}

const useLocationFinder = (options: string[], search?: string, originalIndex?: number, onLocationFired?: (value: LocationValue) => void) => {
  const locationTriggererRef = useRef<string | number>('');
  const { addListener: addLocationChangeListener, notify, listeners: locationListeners } = useLocationListeners<LocationValue>();
  const radius = useLocationRadius(options, originalIndex);
  const searchField = useLocationSearch(search);
  
  useEffect(() => {
    // preventing first render firing
    if( locationTriggererRef.current === '') {
      return;
    }
    const value: LocationValue = {
      searchValue: searchField.searchedValue,
      radiusIndex: radius.radiusIndex,
      coordinates: searchField.coordinates
    };
    onLocationFired?.(value);
    notify(value);
  }, [radius.radiusIndex, searchField.searchedValue, searchField.coordinates, onLocationFired]);

  return {
    radius,
    searchField,
    locationTriggererRef,
    addLocationChangeListener,
    // let's keep it exposed to debug
    locationListeners
  }
}

export { useLocationFinder, useLocationSearch, useLocationRadius };